import { z } from 'zod';

/**
 * DNS module (G12, ADR-0020/0021/0022): panel-managed authoritative DNS on
 * a self-hosted PowerDNS hidden primary, with Plesk-style zone templates
 * and replication to external providers. The panel is the source of truth —
 * these shapes describe panel entities; the PowerDNS wire format lives in
 * the API's powerdns client, never here.
 */

// ── Value vocabulary ────────────────────────────────────────────────

/** A zone apex: an absolute DNS name, stored lowercase without the root dot. */
export const dnsZoneNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => v.replace(/\.$/, ''))
  .pipe(
    z
      .string()
      .min(3)
      .max(253)
      .regex(
        /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
        'Invalid zone name',
      ),
  );

/**
 * A record owner name *relative to its zone*: `@` for the apex, otherwise
 * dot-separated labels that may lead with a `*` wildcard and may contain
 * underscores (SRV/TXT service labels like `_sip._tcp`).
 */
export const dnsRecordNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(200)
  .regex(
    /^(@|(\*\.)?[a-z0-9_](?:[a-z0-9_-]{0,62}[a-z0-9_])?(?:\.[a-z0-9_](?:[a-z0-9_-]{0,62}[a-z0-9_])?)*|\*)$/,
    'Invalid record name (use @ for the zone itself)',
  );

/** A hostname a record points at (CNAME/MX/NS/SRV targets). */
export const dnsTargetNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => v.replace(/\.$/, ''))
  .pipe(
    z
      .string()
      .min(1)
      .max(253)
      .regex(
        /^[a-z0-9_](?:[a-z0-9_-]{0,61}[a-z0-9])?(?:\.[a-z0-9_](?:[a-z0-9_-]{0,61}[a-z0-9])?)*$/,
        'Invalid target hostname',
      ),
  );

/**
 * TTL bounds: floor guards against accidental query storms, ceiling is a
 * week (RFC 1035 operational reality; registries rarely honor more).
 */
export const dnsTtlSchema = z.number().int().min(60).max(604_800);
export const DNS_DEFAULT_TTL = 3600;

export const DNS_RECORD_TYPES = [
  'A',
  'AAAA',
  'CNAME',
  'MX',
  'TXT',
  'SPF',
  'SRV',
  'CAA',
  'NS',
  'PTR',
  'TLSA',
  'DS',
] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

/**
 * Structured per-type record data — what the record editor submits and the
 * shared validation gate. `recordDataToContent` flattens this into the
 * canonical rdata text the panel stores (and PowerDNS serves); SPF is a UI
 * convenience type published as TXT on the wire (RFC 7208 §3.1 deprecated
 * the SPF RRTYPE, and none of the sync providers accept it).
 */
const dnsRecordDataBaseSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('A'), address: z.string().trim().ip({ version: 'v4' }) }),
  z.object({ type: z.literal('AAAA'), address: z.string().trim().ip({ version: 'v6' }) }),
  z.object({ type: z.literal('CNAME'), target: dnsTargetNameSchema }),
  z.object({
    type: z.literal('MX'),
    priority: z.number().int().min(0).max(65_535),
    target: z.union([z.string().trim().pipe(z.literal('.')), dnsTargetNameSchema]),
  }),
  z.object({ type: z.literal('TXT'), text: z.string().min(1).max(4_000) }),
  z.object({
    type: z.literal('SPF'),
    text: z
      .string()
      .trim()
      .min(1)
      .max(450)
      .regex(/^v=spf1(\s|$)/, 'SPF records must start with "v=spf1"'),
  }),
  z.object({
    type: z.literal('SRV'),
    priority: z.number().int().min(0).max(65_535),
    weight: z.number().int().min(0).max(65_535),
    port: z.number().int().min(0).max(65_535),
    target: dnsTargetNameSchema,
  }),
  z.object({
    type: z.literal('CAA'),
    flags: z.number().int().min(0).max(255),
    tag: z.enum(['issue', 'issuewild', 'iodef']),
    value: z.string().trim().min(1).max(500),
  }),
  z.object({ type: z.literal('NS'), target: dnsTargetNameSchema }),
  z.object({ type: z.literal('PTR'), target: dnsTargetNameSchema }),
  z.object({
    type: z.literal('TLSA'),
    usage: z.number().int().min(0).max(3),
    selector: z.number().int().min(0).max(1),
    matchingType: z.number().int().min(0).max(2),
    certificateAssociationData: z
      .string()
      .trim()
      .regex(/^[0-9a-f]+$/i, 'TLSA certificate association data must be hexadecimal')
      .transform((value) => value.toUpperCase()),
  }),
  z.object({
    type: z.literal('DS'),
    keyTag: z.number().int().min(0).max(65_535),
    algorithm: z.number().int().min(0).max(255),
    digestType: z.number().int().min(0).max(255),
    digest: z
      .string()
      .trim()
      .regex(/^[0-9a-f]+$/i, 'DS digest must be hexadecimal')
      .transform((value) => value.toUpperCase()),
  }),
]);
export const dnsRecordDataSchema = dnsRecordDataBaseSchema.superRefine((data, ctx) => {
  if (data.type === 'MX' && data.target === '.' && data.priority !== 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['priority'], message: 'Null MX requires priority 0' });
  }
  if (data.type === 'TLSA') {
    const length = data.certificateAssociationData.length;
    if (length % 2 !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['certificateAssociationData'],
        message: 'TLSA certificate association data must contain complete hex octets',
      });
    }
    const expected = data.matchingType === 1 ? 64 : data.matchingType === 2 ? 128 : null;
    if (expected !== null && length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['certificateAssociationData'],
        message: `TLSA matching type ${data.matchingType} requires ${expected} hex characters`,
      });
    }
  }
  if (data.type === 'DS') {
    if (data.digest.length % 2 !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['digest'],
        message: 'DS digest must contain complete hex octets',
      });
    }
    const knownLengths: Record<number, number> = { 1: 40, 2: 64, 3: 64, 4: 96 };
    const expected = knownLengths[data.digestType];
    if (expected !== undefined && data.digest.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['digest'],
        message: `DS digest type ${data.digestType} requires ${expected} hex characters`,
      });
    }
  }
});
export type DnsRecordData = z.infer<typeof dnsRecordDataSchema>;

/** Quote a character-string for TXT rdata, splitting at 255 octets. */
function quoteTxt(text: string): string {
  const chunks: string[] = [];
  const escaped = text.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  for (let i = 0; i < escaped.length; i += 255) {
    chunks.push(`"${escaped.slice(i, i + 255)}"`);
  }
  return chunks.join(' ');
}

/** Strip TXT quoting back to the raw text (inverse of quoteTxt). */
export function unquoteTxt(content: string): string {
  const parts = content.match(/"((?:[^"\\]|\\.)*)"/g);
  if (!parts) return content;
  return parts
    .map((part) => part.slice(1, -1).replace(/\\(.)/g, '$1'))
    .join('');
}

/**
 * Canonical rdata text for structured record data. Matches PowerDNS's
 * presentation format so panel content and wire content compare equal:
 * hostnames gain the trailing root dot, TXT/SPF text is quoted.
 */
export function recordDataToContent(data: DnsRecordData): string {
  switch (data.type) {
    case 'A':
    case 'AAAA':
      return data.address;
    case 'CNAME':
    case 'NS':
    case 'PTR':
      return `${data.target}.`;
    case 'MX':
      return `${data.priority} ${data.target === '.' ? '.' : `${data.target}.`}`;
    case 'TXT':
    case 'SPF':
      return quoteTxt(data.text);
    case 'SRV':
      return `${data.priority} ${data.weight} ${data.port} ${data.target}.`;
    case 'CAA':
      return `${data.flags} ${data.tag} "${data.value.replace(/"/g, '')}"`;
    case 'TLSA':
      return `${data.usage} ${data.selector} ${data.matchingType} ${data.certificateAssociationData}`;
    case 'DS':
      return `${data.keyTag} ${data.algorithm} ${data.digestType} ${data.digest}`;
  }
}

/** Parse stored rdata text back into editor fields (inverse of recordDataToContent). */
export function contentToRecordData(type: DnsRecordType, content: string): DnsRecordData | null {
  const bare = (v: string) => v.replace(/\.$/, '');
  switch (type) {
    case 'A':
      return { type, address: content };
    case 'AAAA':
      return { type, address: content };
    case 'CNAME':
    case 'NS':
    case 'PTR':
      return { type, target: bare(content) };
    case 'MX': {
      const m = content.match(/^(\d+)\s+(\S+)$/);
      if (!m) return null;
      const parsed = dnsRecordDataSchema.safeParse({ type, priority: Number(m[1]), target: m[2] });
      return parsed.success ? parsed.data : null;
    }
    case 'TXT':
    case 'SPF':
      return { type, text: unquoteTxt(content) };
    case 'SRV': {
      const m = content.match(/^(\d+)\s+(\d+)\s+(\d+)\s+(\S+)$/);
      return m
        ? {
            type,
            priority: Number(m[1]),
            weight: Number(m[2]),
            port: Number(m[3]),
            target: bare(m[4]!),
          }
        : null;
    }
    case 'CAA': {
      const m = content.match(/^(\d+)\s+(issue|issuewild|iodef)\s+"?([^"]*)"?$/);
      return m
        ? { type, flags: Number(m[1]), tag: m[2] as 'issue' | 'issuewild' | 'iodef', value: m[3]! }
        : null;
    }
    case 'TLSA': {
      const m = content.match(/^(\d+)\s+(\d+)\s+(\d+)\s+([0-9a-f]+)$/i);
      if (!m) return null;
      const parsed = dnsRecordDataSchema.safeParse({
        type,
        usage: Number(m[1]),
        selector: Number(m[2]),
        matchingType: Number(m[3]),
        certificateAssociationData: m[4],
      });
      return parsed.success ? parsed.data : null;
    }
    case 'DS': {
      const m = content.match(/^(\d+)\s+(\d+)\s+(\d+)\s+([0-9a-f]+)$/i);
      if (!m) return null;
      const parsed = dnsRecordDataSchema.safeParse({
        type,
        keyTag: Number(m[1]),
        algorithm: Number(m[2]),
        digestType: Number(m[3]),
        digest: m[4],
      });
      return parsed.success ? parsed.data : null;
    }
  }
}

/** Where a panel record came from; drives template re-sync and the editor. */
export const DNS_RECORD_ORIGINS = ['user', 'template', 'auto'] as const;
export type DnsRecordOrigin = (typeof DNS_RECORD_ORIGINS)[number];

// ── Settings (admin) ────────────────────────────────────────────────

export const dnsSettingsSchema = z.object({
  /** PowerDNS API base, e.g. http://powerdns:8081 (docker network) — ADR-0020 §3. */
  apiUrl: z
    .string()
    .trim()
    .url()
    .transform((u) => u.replace(/\/+$/, '')),
  /** X-API-Key. Omit to keep the stored one. */
  apiKey: z.string().trim().min(1).optional(),
  /**
   * Public nameservers written as apex NS records of every new zone — the
   * providers/secondaries actually serving the zone (the hidden primary
   * never appears here, ADR-0020 §2).
   */
  nameservers: z.array(dnsTargetNameSchema).max(8).default([]),
  /** IPv4 that `<ip>` expands to when a zone has no explicit IP. */
  defaultRecordIp: z.string().trim().ip({ version: 'v4' }).nullish(),
});
export type DnsSettingsInput = z.infer<typeof dnsSettingsSchema>;

export interface DnsSettingsDto {
  configured: boolean;
  apiUrl: string | null;
  hasApiKey: boolean;
  nameservers: string[];
  defaultRecordIp: string | null;
}

export interface DnsTestResultDto {
  ok: boolean;
  /** Server version as reported by the PowerDNS API. */
  version: string | null;
  error: string | null;
}

// ── Zones ───────────────────────────────────────────────────────────

export const createDnsZoneSchema = z.object({
  name: dnsZoneNameSchema,
  /** Zone template to apply; null/absent = the default template. */
  templateId: z.string().uuid().nullish(),
  /** IPv4 the template's `<ip>` expands to; falls back to the module default. */
  ip: z.string().trim().ip({ version: 'v4' }).nullish(),
});
export type CreateDnsZoneInput = z.infer<typeof createDnsZoneSchema>;

export const updateDnsZoneSchema = z.object({
  ip: z.string().trim().ip({ version: 'v4' }).nullable(),
});
export type UpdateDnsZoneInput = z.infer<typeof updateDnsZoneSchema>;

export interface DnsRecordDto {
  id: string;
  zoneId: string;
  /** Relative owner name (`@` = apex). */
  name: string;
  /** Fully qualified owner name (no trailing dot). */
  fqdn: string;
  type: DnsRecordType;
  ttl: number;
  content: string;
  /** Structured editor fields; null when content failed to parse. */
  data: DnsRecordData | null;
  origin: DnsRecordOrigin;
  createdAt: string;
}

export interface DnsZoneSyncDto {
  publicationEnabled: boolean;
  providerId: string;
  providerLabel: string;
  providerType: DnsProviderType;
  status: DnsSyncStatus;
  providerZoneId: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  drift: DnsDriftReportDto | null;
  dnssec: DnsProviderDnssecState | null;
}

export interface DnsZoneDto {
  id: string;
  subscriptionId: string;
  name: string;
  powerdnsZoneId: string | null;
  dnssec: boolean;
  /** DS records for registrar delegation (cached from PowerDNS cryptokeys). */
  dsRecords: string[];
  templateId: string | null;
  /** Effective `<ip>` for templates/auto-records (zone override or module default). */
  ip: string | null;
  serial: number | null;
  orphanedAt: string | null;
  createdAt: string;
  recordCount: number;
}

export interface DnsZoneDetailDto extends DnsZoneDto {
  records: DnsRecordDto[];
  syncs: DnsZoneSyncDto[];
}

export interface SubscriptionDnsDto {
  subscriptionId: string;
  featureEnabled: boolean;
  configured: boolean;
  zones: DnsZoneDto[];
}

// ── Records ─────────────────────────────────────────────────────────

export const createDnsRecordSchema = z.object({
  name: z.string().trim().min(1).max(254),
  expectedRevision: z.number().int().min(0).optional(),
  ttl: dnsTtlSchema.default(DNS_DEFAULT_TTL),
  data: dnsRecordDataSchema,
});
export type CreateDnsRecordInput = z.infer<typeof createDnsRecordSchema>;

export const updateDnsRecordSchema = z.object({
  name: z.string().trim().min(1).max(254).optional(),
  expectedRevision: z.number().int().min(0).optional(),
  ttl: dnsTtlSchema.optional(),
  data: dnsRecordDataSchema.optional(),
});
export type UpdateDnsRecordInput = z.infer<typeof updateDnsRecordSchema>;

// ── DNSSEC ──────────────────────────────────────────────────────────

export const dnsZoneDnssecSchema = z.object({ enabled: z.boolean() });
export type DnsZoneDnssecInput = z.infer<typeof dnsZoneDnssecSchema>;

export interface DnsZoneDnssecDto {
  zoneId: string;
  enabled: boolean;
  /** Registrar-ready DS records (one per digest type). */
  dsRecords: string[];
  /**
   * Which providers serve this zone as an unsigned/provider-managed copy
   * (API-pushed zones cannot carry the panel's RRSIGs, ADR-0020 §2).
   */
  unsignedProviders: string[];
}

// ── Zone templates (ADR-0021) ───────────────────────────────────────

/**
 * Template records carry the same structured data as zone records, but
 * string fields may use the `<domain>` and `<ip>` placeholders that expand
 * at application time (Plesk's DNS-template model).
 */
export const dnsTemplateRecordSchema = z.object({
  /**
   * Existing line id. Sending it back keeps the line's identity stable
   * across edits — zone records track lines by id, so an in-place edit
   * updates zones on re-sync instead of reading as remove+add.
   */
  id: z.string().uuid().optional(),
  name: z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(200)
    .regex(
      /^(@|(\*\.)?[a-z0-9_<>.-]+)$/,
      'Invalid template record name (use @ for the zone itself)',
    ),
  type: z.enum(DNS_RECORD_TYPES),
  ttl: dnsTtlSchema.nullish(),
  /** Rdata text; `<domain>`/`<ip>` placeholders allowed anywhere. */
  content: z.string().trim().min(1).max(4_000),
});
export type DnsTemplateRecordInput = z.infer<typeof dnsTemplateRecordSchema>;

export const createDnsTemplateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  isDefault: z.boolean().default(false),
  records: z.array(dnsTemplateRecordSchema).max(100),
});
export type CreateDnsTemplateInput = z.infer<typeof createDnsTemplateSchema>;

export const updateDnsTemplateSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  isDefault: z.boolean().optional(),
  records: z.array(dnsTemplateRecordSchema).max(100).optional(),
});
export type UpdateDnsTemplateInput = z.infer<typeof updateDnsTemplateSchema>;

export interface DnsTemplateRecordDto {
  id: string;
  name: string;
  type: DnsRecordType;
  ttl: number | null;
  content: string;
}

export interface DnsTemplateDto {
  id: string;
  name: string;
  isDefault: boolean;
  records: DnsTemplateRecordDto[];
  zoneCount: number;
  createdAt: string;
}

/** Outcome of a template (re-)application to one zone (ADR-0021 semantics). */
export interface DnsTemplateSyncResultDto {
  zoneId: string;
  added: number;
  updated: number;
  removed: number;
  /** Records the user edited or deleted — preserved exactly, never touched. */
  preserved: number;
}

// ── Providers & sync (ADR-0022) ─────────────────────────────────────

export const DNS_PROVIDER_TYPES = [
  'cloudflare',
  'route53',
  'azure',
  'digitalocean',
  'axfr',
] as const;
export type DnsProviderType = (typeof DNS_PROVIDER_TYPES)[number];

export const DNS_SYNC_STATUSES = ['pending', 'in_sync', 'drift', 'error'] as const;
export type DnsSyncStatus = (typeof DNS_SYNC_STATUSES)[number];

/**
 * Non-secret provider configuration. Secrets (tokens, keys) travel in the
 * separate `credentials` field and are stored encrypted via SettingsStore.
 */
export const dnsProviderConfigSchema = z
  .object({
    /** cloudflare: account id, required only for creating new zones. */
    accountId: z.string().trim().min(1).max(100).optional(),
    /** azure: ARM scope for the DNS zones. */
    subscriptionId: z.string().trim().min(1).max(100).optional(),
    resourceGroup: z.string().trim().min(1).max(100).optional(),
    tenantId: z.string().trim().min(1).max(100).optional(),
    clientId: z.string().trim().min(1).max(100).optional(),
    /** axfr: secondary servers to allow/notify (IP or IP:port for notify). */
    allowAxfrFrom: z.array(z.string().trim().min(1).max(50)).max(16).optional(),
    notifyTargets: z.array(z.string().trim().min(1).max(60)).max(16).optional(),
    /** axfr: secondary hostnames whose SOA serial the drift check queries. */
    checkServers: z.array(dnsTargetNameSchema).max(8).optional(),
    /** axfr: pre-created PowerDNS TSIG key that signs outgoing transfers. */
    tsigKeyName: z.string().trim().min(1).max(100).optional(),
    /** Override the provider API endpoint (tests/self-hosted gateways). */
    apiBase: z.string().trim().url().optional(),
  })
  .strict();
export type DnsProviderConfig = z.infer<typeof dnsProviderConfigSchema>;

export const createDnsProviderSchema = z.object({
  type: z.enum(DNS_PROVIDER_TYPES),
  label: z.string().trim().min(1).max(100),
  config: dnsProviderConfigSchema.default({}),
  /**
   * Provider secret, shape per type: cloudflare/digitalocean = API token;
   * route53 = "accessKeyId:secretAccessKey"; azure = client secret;
   * axfr = TSIG key secret (optional, name in config).
   */
  credentials: z.string().trim().min(1).max(2_000).optional(),
});
export type CreateDnsProviderInput = z.infer<typeof createDnsProviderSchema>;

export const updateDnsProviderSchema = z.object({
  label: z.string().trim().min(1).max(100).optional(),
  config: dnsProviderConfigSchema.optional(),
  credentials: z.string().trim().min(1).max(2_000).optional(),
});
export type UpdateDnsProviderInput = z.infer<typeof updateDnsProviderSchema>;

export interface DnsProviderDto {
  id: string;
  type: DnsProviderType;
  label: string;
  config: DnsProviderConfig;
  hasCredentials: boolean;
  /** Zones currently syncing to this provider. */
  zoneCount: number;
  createdAt: string;
}

export interface DnsProviderTestResultDto {
  ok: boolean;
  error: string | null;
}

/** One record-level difference between the panel zone and a provider copy. */
export interface DnsDriftEntryDto {
  kind: 'missing' | 'extra' | 'changed' | 'unsupported';
  name: string;
  type: string;
  detail: string;
}

export interface DnsDriftReportDto {
  checkedAt: string;
  inSync: boolean;
  missing: number;
  extra: number;
  changed: number;
  unsupported: number;
  /** Record types the provider cannot host; listed, never silently dropped. */
  skippedUnsupported: string[];
  /** First differences, capped — enough to act on, small enough to store. */
  entries: DnsDriftEntryDto[];
}

export interface DnsProviderDnssecState {
  mode: 'provider-managed' | 'panel-signatures';
  /** Public registrar-ready DS values for the provider's signer. */
  dsRecords: string[];
  verifiedAt: string;
}

export const dnsProviderDnssecInputSchema = z.object({
  mode: z.enum(['provider-managed', 'panel-signatures']),
  dsRecords: z
    .array(
      z.string().trim().transform((value, ctx) => {
        const parsed = contentToRecordData('DS', value);
        if (!parsed || parsed.type !== 'DS') {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid DS record' });
          return z.NEVER;
        }
        return recordDataToContent(parsed);
      }),
    )
    .max(16),
}).superRefine((value, ctx) => {
  if (value.mode === 'provider-managed' && value.dsRecords.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dsRecords'], message: 'Provider-managed DNSSEC requires at least one DS record' });
  }
  if (value.mode === 'panel-signatures' && value.dsRecords.length > 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dsRecords'], message: 'Panel-signature targets use the panel DS set' });
  }
});

export const dnsZoneSyncToggleSchema = z.object({
  enabled: z.boolean(),
  dnssec: dnsProviderDnssecInputSchema.nullish(),
});
export type DnsZoneSyncToggleInput = z.infer<typeof dnsZoneSyncToggleSchema>;

/** Prepare an existing public Route 53 zone without starting publication. */
export const dnsRoute53PrepareSchema = z.object({
  providerZoneId: z.string().trim().regex(/^[A-Z0-9]{1,64}$/),
}).strict();

export const dnsPublicationSchema = z.object({
  enabled: z.boolean(),
  sourcePublisherStopped: z.boolean().default(false),
}).strict();

// ── Module status & reconciliation ──────────────────────────────────

export interface DnsReconcileStats {
  zonesChecked: number;
  zonesCreated: number;
  zonesRelinked: number;
  zonesOrphaned: number;
  rrsetsRepaired: number;
  autoRecordsAdded: number;
  autoRecordsRemoved: number;
  /** Auto-records skipped because a user/template record owns name+type. */
  autoRecordsBlocked: number;
  dsRefreshed: number;
  /** PowerDNS zones no panel row claims (reported, never auto-deleted). */
  unmanagedZones: string[];
}

export interface DnsReconcileRunDto {
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: 'success' | 'failed';
  error: string | null;
  stats: DnsReconcileStats | null;
}

export interface DnsStatusDto {
  configured: boolean;
  featureKey: string;
  running: boolean;
  lastReconcile: DnsReconcileRunDto | null;
  zoneCount: number;
  recordCount: number;
  providerCount: number;
}

/** Customer record management is distinct from DNS service availability. */
export const DNS_MANAGE_RECORDS_FEATURE = 'dns_manage_records';
export interface CustomerDnsZoneDto { id: string; name: string; records: DnsRecordDto[]; }
